import { SumStoreysFromDesignExpressionVariablesResult } from '@shared/helpers/design/expressions/storey-sum-from-design.helpers';
import type { StoreySelector } from '@shared/helpers/storeys.helpers';
import {
  ActivityType,
  EnsureActivityMap,
} from './activities/activity-type.model';
import { ActivityPropertyMap } from './activities/activity.model';
import { Coordinate } from '../geography.model';
import { ActivityId } from '../activity-class.model';
import { ProductID } from '../product.model';

export type StoreyExpressionVariables = {
  /**
   * Name of the storey ( generated if not provided)
   */
  name: string;

  /**
   * Gross floor area of the storey
   */
  gfa: number;

  /**
   * Height of the storey
   */
  height: number;

  /**
   * Inner height of the storey (old)
   */
  inner_height: number;

  /**
   * Elevation of the storey
   */
  elevation: number;

  /**
   * Exposed outer perimeter of the storey, including courtyard inner edges.
   * Party walls are subtracted in proportion to overlapping height so
   * {@link StoreyExpressionVariables.gwa} equals `perimeter * height`.
   */
  perimeter: number;

  /**
   * Sum of inner perimeters from cutout shapes on the storey.
   */
  perimeter_inner?: number;

  /**
   * Whether the storey is a mezzanine
   */
  mezzanine: boolean;

  /**
   * External wall area (m²) after subtracting party-wall area shared with
   * neighbouring buildings at overlapping height.
   */
  gwa: number;

  /**
   * 1 when this storey sits on grade (no other solid footprint strictly below).
   * 0 when it sits on another storey — including another building. A void
   * (cutout) below still counts as not on grade.
   */
  is_ground_floor?: number;

  /**
   * Net floor area of storeys (any building) whose top touches this storey's
   * bottom. Cutouts are subtracted so a slab over a courtyard is not cover.
   */
  covered_gfa_below?: number;

  /**
   * Net floor area of storeys (any building) whose bottom touches this storey's
   * top. Cutouts are subtracted so sky through an atrium remains uncovered.
   */
  covered_gfa_above?: number;

  /**
   * Variables for each activity of the building
   */
  programs: ActivityExpressionVariablesRecord;
};

/**
 * Variables exposed from {@link GetStoreyFn} (summed / selected storeys).
 */
export type StoreySummaryVariables = {
  /** Summary of the selected storeys gross floor area */
  gfa: number;

  /** Summary of the selected storeys inner height */
  inner_height: number;

  /** Maximum perimeter of any selected storey */
  perimeter: number;

  /**
   * External wall area after subtracting party walls at overlapping height.
   * Differs from {@link StoreySummaryVariables.external_gwa} when neighbours
   * do not share the full storey height.
   */
  gwa: number;

  gfa_above_ground: number;
  total_height: number;
  external_gwa: number;
  external_gwa_above_ground: number;
  gfa_floor_slabs: number;
  /** Roof / uncovered top slab area of the selected storeys */
  gfa_sky: number;
  /** Ground-contact floor slab area of the selected storeys */
  gfa_ground_floor_slabs: number;
  gwa_stairwell_parting_walls: number;
  gwa_apartment_parting_internal_walls: number;
  gwa_elevator_shaft_walls: number;
};

/**
 * Function to get summed expression variables for selected storeys.
 * Numeric selectors are 1-based (`getStorey(1)` is the first storey). Index 0
 * throws. No selectors, or only blank selectors, means the site-wide summary.
 * Numeric indexes pick that floor from every building in scope and combine
 * them (sum GFA/GWA/perimeter, max height).
 * {@link BuildingExpressionVariables.getStorey} uses the same name and rules
 * on the buildings returned by {@link GetBuildingsFn}.
 */
export type GetStoreyFn = (
  ...selectors: StoreySelector[]
) => StoreySummaryVariables;

/**
 * One building's summed storeys plus {@link GetStoreyFn} scoped to that
 * selection. Returned by {@link GetBuildingsFn}. `getStorey` matches the
 * site-level function name. `storeys_count` is the floor count of the
 * selected stacks (tallest when several buildings are selected).
 */
export type BuildingExpressionVariables = StoreySummaryVariables & {
  name: string;
  storeys_count: number;
  getStorey: GetStoreyFn;
};

/**
 * Function to get summed expression variables for selected buildings (by
 * 1-based index, name, or id). `getBuildings(1)` is the first building.
 * No selectors returns the same site totals as {@link GetStoreyFn} with no
 * selectors. Nested `getStorey` matches the site-level function; `storeys_count`
 * is the floor count of the selected stacks (tallest when several).
 */
export type GetBuildingsFn = (
  ...selectors: Array<number | string>
) => BuildingExpressionVariables;

/**
 * Function to check if a certain id is the current activity
 */
export type IsActivityFn = (idOrType: ActivityId | ActivityType) => 1 | 0;

/**
 * Functions that can be accessed from an expression
 */
export type DesignExpressionFunctions = {
  getStorey: GetStoreyFn;
  getBuildings: GetBuildingsFn;
  isActivity: IsActivityFn;
};

/**
 * Variables for products
 */
export type DesignExpressionProductVariables = {
  densityRecord: Record<ProductID, number>;
  densityArray: number[];
  densityPerSqmRecord: Record<ProductID, number>;
  densityPerSqmArray: number[];
  productIds: ProductID[];
};

/**
 * Expression variables genereated from a design.
 */
export type DesignExpressionVariables = StoreySummaryVariables & {
  /**
   * The location of the building
   */
  location: Coordinate;

  /**
   * Design-level activity variables (not returned from {@link GetStoreyFn}).
   */
  programs: ActivityExpressionVariablesRecord;

  /**
   * Variables for each storey of the first building. `storeys[n]` (math.js
   * 1-based) walks that stack. Numeric {@link DesignExpressionFunctions.getStorey}
   * indexes instead sum that floor across {@link DesignExpressionVariables.storeysByBuilding}.
   * Use {@link GetBuildingsFn} when a formula must stay on one building.
   */
  storeys: StoreyExpressionVariables[];

  /**
   * Storey stacks per building. {@link DesignExpressionFunctions.getStorey}
   * sums matching floors across every stack. Project `storeys_count` is the
   * tallest stack so `getStorey(storeys_count)` stays the last-floor idiom.
   */
  storeysByBuilding?: StoreyExpressionVariables[][];

  /**
   * Legacy-compatible storey sum output ({@link sumStoreys}) calculated from the design.
   * This is used to keep existing expression variables stable during the migration.
   */
  storeySum?: SumStoreysFromDesignExpressionVariablesResult;

  /**
   * Pass on the ActivityType enum to be used in expressions.
   */
  ActivityType: Record<keyof typeof ActivityType, ActivityType>;
} & DesignExpressionFunctions;

export const emptyStoreySummaryVariables: StoreySummaryVariables =
  Object.freeze({
    gfa: 0,
    inner_height: 0,
    perimeter: 0,
    gwa: 0,
    gfa_above_ground: 0,
    total_height: 0,
    external_gwa: 0,
    external_gwa_above_ground: 0,
    gfa_floor_slabs: 0,
    gfa_sky: 0,
    gfa_ground_floor_slabs: 0,
    gwa_stairwell_parting_walls: 0,
    gwa_apartment_parting_internal_walls: 0,
    gwa_elevator_shaft_walls: 0,
  });

export type DefaultActivityReferenceExpressionVariables = {
  /**
   * All activities have a gross floor area.
   * TODO: Is this really a gfa and not net area if we account for facades and installations etc
   */
  gfa: number;
};

type ResidentialActivityReferenceExpressionVariables =
  DefaultActivityReferenceExpressionVariables & {
    apartments_living_area_per_apartment: number;
    apartments_balcony_area_per_apartment: number;
    apartments_bathroom_area_per_apartment: number;
  };

type StairwellsActivityReferenceExpressionVariables =
  DefaultActivityReferenceExpressionVariables & {
    stairwells_apartments_per_stairwell_per_storey: number;
    stairwells_elevators_per_stairwell: number;
    gfa_openings_per_stairwell_per_storey: number;
    gfa_elevator_per_stairwell_per_storey: number;
    gfa_floor_per_stairwell_per_storey: number;
    gfa_per_stairwell_per_storey: number;
    gfa_stairwell_per_living_area: number;
    gfa_stairs_per_gfa_stairwell: number;
    gfa_floor_per_gfa_stairwell: number;
    gfa_stairwell_per_gfa_apartment: number;
  };

type ActivityReferenceExpressionVariablesMap = EnsureActivityMap<{
  [ActivityType.Residential]: ResidentialActivityReferenceExpressionVariables;
  [ActivityType.Stairwells]: StairwellsActivityReferenceExpressionVariables;
  // [ActivityType.LaundryRoom]: DefaultActivityReferenceExpressionVariables;
  // [ActivityType.BikeStorage]: DefaultActivityReferenceExpressionVariables;
  [ActivityType.Storage]: DefaultActivityReferenceExpressionVariables;
  [ActivityType.Utilities]: DefaultActivityReferenceExpressionVariables;
  [ActivityType.Garage]: DefaultActivityReferenceExpressionVariables;
  [ActivityType.Other]: DefaultActivityReferenceExpressionVariables;
  [ActivityType.Unset]: DefaultActivityReferenceExpressionVariables;
  [ActivityType.Industry]: DefaultActivityReferenceExpressionVariables;
  [ActivityType.Office]: DefaultActivityReferenceExpressionVariables;
  [ActivityType.Innerwalls]: DefaultActivityReferenceExpressionVariables;
  // [ActivityType.Warehouse]: DefaultActivityReferenceExpressionVariables;
  // [ActivityType.Parking]: DefaultActivityReferenceExpressionVariables;
  [ActivityType.Retail]: DefaultActivityReferenceExpressionVariables;
  // [ActivityType.Restaurant]: DefaultActivityReferenceExpressionVariables;
  [ActivityType.School]: DefaultActivityReferenceExpressionVariables;
  // [ActivityType.Hospital]: DefaultActivityReferenceExpressionVariables;
  // [ActivityType.Hotel]: DefaultActivityReferenceExpressionVariables;
  [ActivityType.Facades]: DefaultActivityReferenceExpressionVariables;
  [ActivityType.Installations]: DefaultActivityReferenceExpressionVariables;
  // [ActivityType.Elevators]: DefaultActivityReferenceExpressionVariables;
}>;

/**
 * Expression variables for an activity reference. Basically the properties + gfa
 */
export type ActivityReferenceExpressionVariables<Type extends ActivityType> =
  ActivityPropertyMap[Type] & ActivityReferenceExpressionVariablesMap[Type];

/**
 * Record of expression variables for all activity types.
 */
export type ActivityExpressionVariablesRecord = {
  [type in ActivityType]: ActivityReferenceExpressionVariables<type>;
};
